import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        <img src="/cluubz-logo-white.png" alt="Cluubz" className="legal-logo" />
      </div>

      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: January 2026</p>

        <section>
          <h2>Introduction</h2>
          <p>
            Cluubz ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy
            explains how we collect, use, disclose, and safeguard your information when you use our
            mobile application and website (collectively, the "Service").
          </p>
          <p>
            Please read this Privacy Policy carefully. By using the Service, you agree to the collection
            and use of information in accordance with this policy.
          </p>
        </section>

        <section>
          <h2>Information We Collect</h2>

          <h3>Personal Information</h3>
          <p>We may collect the following personal information:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account</li>
            <li><strong>Child Information:</strong> Your children's names and birth dates to help organise their activities</li>
            <li><strong>Activity Data:</strong> Information about activities, schedules, clubs, and events you create or participate in</li>
            <li><strong>Profile Photos:</strong> Optional photos you upload for profiles or activities</li>
          </ul>

          <h3>Automatically Collected Information</h3>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system, and unique device identifiers</li>
            <li><strong>Usage Data:</strong> How you interact with the app, features used, and time spent</li>
            <li><strong>Location Data:</strong> With your permission, approximate location to suggest nearby activities (optional)</li>
          </ul>

          <h3>Third-Party Integrations</h3>
          <p>
            When you connect third-party services (such as Pitchero, SOCS, SchoolsBuddy, or school portals),
            we access activity and schedule data from these services to display in Cluubz. We only access
            the minimum information necessary to provide the service.
          </p>
        </section>

        <section>
          <h2>How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Display your children's activities and schedules</li>
            <li>Send notifications about schedule changes and activity updates</li>
            <li>Connect you with other parents for coordinating activities</li>
            <li>Sync data from connected third-party services</li>
            <li>Improve and personalise your experience</li>
            <li>Respond to your enquiries and support requests</li>
            <li>Send important updates about the Service</li>
          </ul>
        </section>

        <section>
          <h2>Data Sharing</h2>
          <p>We may share your information in the following circumstances:</p>
          <ul>
            <li><strong>With Other Users:</strong> When you connect with other parents, they can see your name and shared activity information</li>
            <li><strong>Service Providers:</strong> We use third-party services to help operate our Service (hosting, analytics, etc.)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          </ul>
          <p><strong>We do not sell your personal information to third parties.</strong></p>
        </section>

        <section>
          <h2>Data Security</h2>
          <p>
            We implement appropriate technical and organisational measures to protect your personal
            information against unauthorised access, alteration, disclosure, or destruction. However,
            no method of transmission over the Internet is 100% secure.
          </p>
        </section>

        <section>
          <h2>Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed to
            provide you with the Service. You can request deletion of your account and associated data
            at any time by contacting us.
          </p>
        </section>

        <section>
          <h2>Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Delete your account and data</li>
            <li>Export your data</li>
            <li>Withdraw consent for optional data collection (e.g., location)</li>
            <li>Object to certain processing of your data</li>
          </ul>
          <p>To exercise these rights, please contact us at support@cluubz.com.</p>
        </section>

        <section>
          <h2>Children's Privacy</h2>
          <p>
            The Service is designed for parents to manage their children's activities. We collect
            children's names and birth dates solely to help parents organise schedules. We do not
            knowingly collect personal information directly from children under 13.
          </p>
        </section>

        <section>
          <h2>Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to improve your experience, analyse usage, and
            remember your preferences. You can control cookies through your browser settings.
          </p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes
            by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>If you have any questions about this Privacy Policy, please contact us:</p>
          <ul>
            <li>Email: support@cluubz.com</li>
            <li>Website: <a href="https://www.cluubz.com">https://www.cluubz.com</a></li>
          </ul>
        </section>
      </div>

      <div className="legal-footer">
        <p>&copy; {new Date().getFullYear()} Cluubz. All rights reserved.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
